import { createSelector } from 'reselect'
import { isV4Routed } from 'lib/api/ssapi/secondStepApi.router'
import { adaptSitesV4ToLegacySites } from 'lib/api/adapters/sites'
import ssapiSelectors from 'store/ssapiManager/selectors'
import { normalizeClaims } from 'store/siteManager/helpers'
import {
  isDwbEnabledAtSite,
  isProgramEnabledAtSite,
  isStandaloneDwbEligibleSite,
} from './siteProgramAccess'

const findAdminLicenseIds = accessObj => {
  const filterKeys = [
    'k5SelDigital',
    'mspDigital',
    'highSchool',
    'sela',
    'bpuDigital',
    'cpuDigital',
  ]
  const adminLicenseIds = filterKeys
    .map(key => {
      if (!accessObj[key]) return

      const product = accessObj[key]
      const licenseIds = Object.values(product)
        .map(({ admin, family, leader }) => {
          if (key !== 'highSchool' && admin?.hasRole) {
            return admin.licenseIds
          } else if (key !== 'highSchool' && leader?.hasRole) {
            return leader.licenseIds
          } else if (
            key === 'highSchool' &&
            (admin?.hasRole || family?.hasRole)
          ) {
            return [...(family?.licenseIds || []), ...(admin?.licenseIds || [])]
          }
        })
        .filter(item => item)
        .flat()

      return licenseIds
    })
    .filter(item => item)
    .flat()

  return [...new Set(adminLicenseIds)]
}

const siteHasAdminClaim = site =>
  normalizeClaims(site?.claims).includes('Admin')

const siteHasAdminLicenseAccess = (site, accessibleAdminLicenseIds) => {
  if (!Array.isArray(site?.licenses) || !accessibleAdminLicenseIds?.length) {
    return false
  }
  const idSet = new Set(accessibleAdminLicenseIds.map(String))
  return site.licenses.some(
    ({ id, isExpired }) => !isExpired && id != null && idSet.has(String(id)),
  )
}

const siteHasAdminAccess = (site, accessibleAdminLicenseIds) =>
  siteHasAdminLicenseAccess(site, accessibleAdminLicenseIds) ||
  siteHasAdminClaim(site)

/**
 * @deprecated To be removed via LEARN-16518
 */
const filterAdminK8Licenses = (accessObj, activeDigitalLicenses) => {
  if (activeDigitalLicenses?.length === 0) return null
  const k8Types = ['K8P', 'MSP']
  const activeAdminLicenses = filterAdminLicenses(
    accessObj,
    activeDigitalLicenses,
  )
  const activeAdminK8Licenses = activeAdminLicenses?.filter(l =>
    k8Types.includes(l.type),
  )
  return activeAdminK8Licenses
}

/**
 * @deprecated To be removed via LEARN-16518
 */
const filterAdminLicenses = (accessObj, activeDigitalLicenses) => {
  if (activeDigitalLicenses?.length === 0) return null
  const adminLicenseIds = findAdminLicenseIds(accessObj)
  const filteredLicenses = activeDigitalLicenses
    ?.filter(({ licenseId }) =>
      adminLicenseIds.find(
        adminLicenseId => adminLicenseId === licenseId?.toString(),
      ),
    )
    .sort((a, b) => a['siteName']?.localeCompare(b['siteName']))

  return filteredLicenses
}

// This maps v3 licenses to v2 for pages that are managed by long-lived feature flags that we couldn't put behind v3 feature flags
const selectActiveDigitalLicenses = createSelector(
  [
    state => state.licenseManager.activeDigitalLicenses,
    state => state.licenseManager.sites,
    state => state.licenseManager.isFetchingActiveDigitalLicenses,
    state => state.licenseManager.isFetchingUserSites,
    state => state.licenseManager.error,
    state => state.ssapiManager?.sites,
    state => state.ssapiManager?.netSuiteAssets,
  ],
  (
    v3ActiveLicenses,
    v3Sites,
    isFetchingLicenses,
    isFetchingSites,
    error,
    v4SitesRaw,
    v4AssetsMap,
  ) => {
    // Prefer v4 when routed and available
    const useV4Sites = isV4Routed('getUserSites') && Array.isArray(v4SitesRaw)
    const useV4Assets =
      isV4Routed('getNetSuiteAssetsForUser') &&
      v4AssetsMap &&
      typeof v4AssetsMap === 'object'

    const userSites = useV4Sites
      ? adaptSitesV4ToLegacySites(v4SitesRaw)
      : v3Sites

    let activeLicenses = v3ActiveLicenses

    // TODO: Move to the adapter in ss api folder
    if (useV4Assets || useV4Sites) {
      const fromSites = useV4Sites
        ? (userSites || []).flatMap(site =>
            Array.isArray(site.licenses) ? site.licenses : [],
          )
        : []

      activeLicenses = fromSites
    }

    if (!activeLicenses?.length) return activeLicenses

    const shouldNotFetch = !!error || isFetchingSites || isFetchingLicenses
    const isNotHydrated = !userSites || !activeLicenses
    if (shouldNotFetch) {
      return {} // this return value can be checked in components that use this selector to prevent refetching if error or fetching in progress
    }
    if (isNotHydrated) {
      return null
    }

    const activeDigitalLicenses = activeLicenses?.map(
      ({ id, product, site, expirationDate }) => {
        // claims may be empty if the site is not returned by /sites/me
        const { claims = [] } =
          userSites?.find(({ id }) => id === site.id) || {}
        return {
          claims,
          licenseId: id,
          product: product.description,
          productType: product.type || 'BULK',
          programCodes: product.programCodes,
          siteId: site.id,
          siteName: site.name,
          type: product.skuCode,
          expirationDate,
          sku: product.sku?.skuValue,
        }
      },
    )
    return activeDigitalLicenses
  },
)

const selectActiveSites = createSelector(
  [
    ssapiSelectors.selectSitesFromSsapi,
    state => state.licenseManager?.sites,
    state => state.licenseManager?.isFetchingUserSites,
    state => state.licenseManager?.error,
    state => state.ssapiManager?.isFetchingUserSites,
    state => state.ssapiManager?.sites,
  ],
  (
    ssapiSites,
    legacySites,
    isFetchingUserSites,
    error,
    isFetchingSsapiSites,
    v4SitesRaw,
  ) => {
    const shouldNotFetch =
      !!error || isFetchingUserSites || isFetchingSsapiSites
    // Prefer sites from ssapiManager through translation layer, fallback to legacy
    // When ssapiSites is null (due to fetching), check if raw v4 sites exist and adapt them
    let sites = ssapiSites !== null ? ssapiSites : legacySites
    if (sites === null && Array.isArray(v4SitesRaw)) {
      sites = adaptSitesV4ToLegacySites(v4SitesRaw)
    }
    const isNotHydrated = !sites

    // Even if fetching, prefer returning the best-known active sites array (avoid {}) to keep consumers rendering
    if (shouldNotFetch && !isNotHydrated) {
      const activeDuringFetch = (sites || [])
        .filter(site => site?.licenses?.some(({ isExpired }) => !isExpired))
        .sort((a, b) => a['name']?.localeCompare(b['name']))
      return activeDuringFetch
    }
    if (isNotHydrated) {
      return null
    }

    const activeSites = (sites || [])
      .filter(site => site?.licenses?.some(({ isExpired }) => !isExpired))
      .sort((a, b) => a['name']?.localeCompare(b['name']))
    return activeSites
  },
)

export const selectActiveAdminSites = createSelector(
  [selectActiveSites, state => state.userAccessManager?.access],
  (activeSites, userAccess) => {
    const isNotSettled = !Array.isArray(activeSites) || !userAccess
    if (isNotSettled) {
      return activeSites
    }
    const accessibleAdminLicenseIds = findAdminLicenseIds(userAccess)

    return activeSites.filter(site =>
      siteHasAdminAccess(site, accessibleAdminLicenseIds),
    )
  },
)

const selectActiveAdminHsSites = createSelector(
  [selectActiveAdminSites],
  activeAdminSites => {
    const isNotSettled = !Array.isArray(activeAdminSites)
    if (isNotSettled) {
      return activeAdminSites
    }
    const hsSites = activeAdminSites.filter(site =>
      isProgramEnabledAtSite(site, 'HS'),
    )
    return hsSites
  },
)

const selectActiveAdminK8Sites = createSelector(
  [selectActiveAdminSites],
  activeAdminSites => {
    const isNotSettled = !Array.isArray(activeAdminSites)
    if (isNotSettled) {
      return activeAdminSites
    }
    const k8Sites = activeAdminSites.filter(site =>
      isProgramEnabledAtSite(site, 'K8'),
    )
    return k8Sites
  },
)

/** Active admin sites eligible for standalone Digital Well-Being program. */
export const selectActiveAdminStandaloneDwbSites = createSelector(
  [selectActiveAdminSites],
  activeAdminSites => {
    if (!Array.isArray(activeAdminSites)) return []
    return activeAdminSites.filter(isStandaloneDwbEligibleSite)
  },
)

/** Active K-8 admin sites with Digital Well-Being enabled (site preference). */
export const selectActiveAdminK8SitesWithDwb = createSelector(
  [selectActiveAdminK8Sites],
  activeAdminK8Sites => {
    if (!Array.isArray(activeAdminK8Sites)) return []
    return activeAdminK8Sites.filter(isDwbEnabledAtSite)
  },
)

/** Active high school admin sites with Digital Well-Being enabled (site preference). */
export const selectActiveAdminHsSitesWithDwb = createSelector(
  [selectActiveAdminHsSites],
  activeAdminHsSites => {
    if (!Array.isArray(activeAdminHsSites)) return []
    return activeAdminHsSites.filter(isDwbEnabledAtSite)
  },
)

const selectActiveAdminSitePreferences = createSelector(
  [selectActiveAdminSites],
  activeSites => {
    const isNotSettled = !Array.isArray(activeSites)
    if (isNotSettled) {
      return activeSites
    }
    const sitePreferences = activeSites.reduce((map, site) => {
      map[site.id] = site.sitePreferences
      return map
    }, {})
    return sitePreferences
  },
)

const selectActiveAdminBpuSites = createSelector(
  [selectActiveAdminSites],
  activeAdminSites => {
    if (!Array.isArray(activeAdminSites)) return activeAdminSites
    return activeAdminSites.filter(site => isProgramEnabledAtSite(site, 'BPU'))
  },
)

const selectActiveAdminCpuSites = createSelector(
  [selectActiveAdminSites],
  activeAdminSites => {
    if (!Array.isArray(activeAdminSites)) return activeAdminSites
    return activeAdminSites.filter(site => isProgramEnabledAtSite(site, 'CPU'))
  },
)

const selectActiveAdminSelaSites = createSelector(
  [selectActiveAdminSites],
  activeAdminSites => {
    if (!Array.isArray(activeAdminSites)) return activeAdminSites
    return activeAdminSites.filter(site => isProgramEnabledAtSite(site, 'SELA'))
  },
)

export default {
  filterAdminK8Licenses,
  filterAdminLicenses,
  findAdminLicenseIds,
  selectActiveAdminBpuSites,
  selectActiveAdminCpuSites,
  selectActiveAdminHsSites,
  selectActiveAdminHsSitesWithDwb,
  selectActiveAdminK8Sites,
  selectActiveAdminK8SitesWithDwb,
  selectActiveAdminSelaSites,
  selectActiveAdminStandaloneDwbSites,
  selectActiveAdminSites,
  selectActiveDigitalLicenses,
  selectActiveSites,
  selectActiveAdminSitePreferences,
}
